export const toLocalISODate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const isWholeNumberValue = (value: unknown): boolean => {
  if (value === "" || value === null || value === undefined) return true;
  const num = Number(value);
  if (!Number.isFinite(num)) return false;
  return Number.isInteger(num);
};

export const blockDecimalNumberInput = (
  e: React.KeyboardEvent<HTMLInputElement>,
) => {
  if (e.key === "." || e.key === "," || e.key === "e" || e.key === "E") {
    e.preventDefault();
  }
};

export const blockDecimalPaste = (
  e: React.ClipboardEvent<HTMLInputElement>,
) => {
  const pasteData = e.clipboardData.getData("text");
  if (/[.,eE]/.test(pasteData)) {
    e.preventDefault();
  }
};

/**
 * Amounts that carry two decimals: salary, allowance, working hours and
 * vacation or sick days. A third decimal is rejected rather than silently
 * rounded, so what the user typed is what gets stored.
 */
export const isTwoDecimalValue = (value: unknown): boolean => {
  if (value === "" || value === null || value === undefined) return true;
  const num = Number(value);
  if (!Number.isFinite(num)) return false;
  const scaled = num * 100;
  return Math.abs(scaled - Math.round(scaled)) < 1e-9;
};

/**
 * Reads a typed amount, accepting the comma a German keyboard produces.
 * Number("12,5") is NaN, so the separator has to be normalised first.
 */
export const parseAmountInput = (value: unknown): number => {
  if (typeof value === "number") return value;
  const text = String(value ?? "").trim().replace(",", ".");
  if (text === "") return 0;
  const parsed = Number(text);
  return Number.isFinite(parsed) ? parsed : NaN;
};

/** Reads an amount without rounding it to a whole number. */
export const toOptionalAmount = (value: unknown): number | null => {
  if (value === "" || value === null || value === undefined) return null;
  const num = Number(value);
  if (!Number.isFinite(num)) return null;
  return Math.round(num * 100) / 100;
};

/**
 * Blocks everything but digits and a single decimal separator. Amount fields
 * accept a comma too, which is what a German keyboard produces.
 */
export const blockNonAmountInput = (
  e: React.KeyboardEvent<HTMLInputElement>,
) => {
  if (e.key === "e" || e.key === "E") {
    e.preventDefault();
  }
};

export const isQuarterHourValue = (value: unknown): boolean => {
  if (value === "" || value === null || value === undefined) return true;
  const num = Number(value);
  if (!Number.isFinite(num)) return false;
  const minutes = Math.round(num * 60 * 100) / 100;
  return Math.abs(minutes - Math.round(minutes)) < 0.001;
};

export const toOptionalInteger = (value: unknown): number | null => {
  if (value === "" || value === null || value === undefined) return null;
  const num = Number(value);
  return Number.isFinite(num) ? Math.round(num) : null;
};

export const toOptionalQuarterHour = (value: unknown): number | null => {
  if (value === "" || value === null || value === undefined) return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

export const findNonIntegerFieldLabel = (
  fields: Array<{ label: string; value: unknown }>,
): string | null => {
  for (const field of fields) {
    if (!isWholeNumberValue(field.value)) {
      return field.label;
    }
  }
  return null;
};

export const isEqualValue = (a: unknown, b: unknown): boolean => {
  if (a === b) return true;
  if ((a === "" || a === null || a === undefined) && (b === "" || b === null || b === undefined)) {
    return true;
  }
  return String(a ?? "").trim() === String(b ?? "").trim();
};

export const sameIdSet = (a: unknown[], b: unknown[]): boolean => {
  const setA = new Set((a || []).map(String));
  const setB = new Set((b || []).map(String));
  if (setA.size !== setB.size) return false;
  for (const item of setA) {
    if (!setB.has(item)) return false;
  }
  return true;
};

export const getReadableErrorMessage = (
  error: unknown,
  fallback = "Ein unbekannter Fehler ist aufgetreten.",
): string => {
  if (!error) return fallback;
  if (typeof error === "string") return error;
  if (typeof error === "object" && error !== null) {
    const apolloErr = error as {
      graphQLErrors?: Array<{ message?: string }>;
      networkError?: {
        result?: { errors?: Array<{ message?: string }> };
        message?: string;
      };
      message?: string;
    };

    if (apolloErr.graphQLErrors && apolloErr.graphQLErrors.length > 0) {
      const messages = apolloErr.graphQLErrors
        .map((g) => g?.message?.trim())
        .filter((msg): msg is string => Boolean(msg));
      if (messages.length > 0) {
        return messages.join(" | ");
      }
    }

    if (
      apolloErr.networkError?.result?.errors &&
      apolloErr.networkError.result.errors.length > 0
    ) {
      const messages = apolloErr.networkError.result.errors
        .map((e) => e?.message?.trim())
        .filter((msg): msg is string => Boolean(msg));
      if (messages.length > 0) {
        return messages.join(" | ");
      }
    }

    if (
      "message" in error &&
      typeof (error as { message: unknown }).message === "string"
    ) {
      const rawMessage = (error as { message: string }).message.trim();
      const cleaned = rawMessage.replace(/^ApolloError:\s*/i, "");
      if (cleaned) {
        return cleaned;
      }
    }
  }
  return fallback;
};

/**
 * Imported Seller-Math category keys stay technical in the database so
 * existing task selections and later syncs remain stable. Only their UI
 * label is translated to the terminology used in Seller Math.
 */
const SELLER_MATH_CATEGORY_LABELS: Record<string, string> = {
  activity: "Aufgaben / Tätigkeiten",
  cost_structure: "Kostenstruktur",
  platform: "Plattform",
  delivery_type: "Lieferart",
  shop: "Firma",
  customer_group: "Kundengruppe",
  distribution: "Verteilung",
  payment_type: "Zahlungsart",
  shipping_type: "Versandart",
  domain: "Domain",
  customer_category: "Kundenkategorie",
  invoice_country: "Rechnungsland",
  shipping_country: "Lieferland",
  sku: "SKU",
  customer_no: "Kundennummer",
};

export const formatCategoryTypeLabel = (value: string): string => {
  if (!value) return "";
  const normalized = value.trim().toLowerCase();
  if (SELLER_MATH_CATEGORY_LABELS[normalized]) {
    return SELLER_MATH_CATEGORY_LABELS[normalized];
  }
  return value.charAt(0).toUpperCase() + value.slice(1).replaceAll("_", " ");
};

const OPEN_END = "9999-12-31";

const addOneDay = (isoDate: string): string => {
  const parsed = new Date(`${isoDate}T00:00:00Z`);
  parsed.setUTCDate(parsed.getUTCDate() + 1);
  return parsed.toISOString().slice(0, 10);
};

const subtractOneDay = (isoDate: string): string => {
  const parsed = new Date(`${isoDate}T00:00:00Z`);
  parsed.setUTCDate(parsed.getUTCDate() - 1);
  return parsed.toISOString().slice(0, 10);
};

export const formatGermanDate = (isoDate: string): string => {
  const [year, month, day] = isoDate.split("-");
  return year && month && day ? `${day}.${month}.${year}` : isoDate;
};

/**
 * Stretches between periods that none of them covers.
 *
 * Mirrors `find_coverage_gaps` on the server. These are not errors: while a
 * plan is built one period at a time there are always gaps, and the base
 * value covers them. They are surfaced so a forgotten month is visible
 * instead of quietly falling back.
 *
 * Only the span between the first and last period is examined; before and
 * after that is ordinary fallback.
 */
export const findPeriodGaps = (
  periods: Array<{ fromDate: string; toDate: string | null }>,
): Array<{ fromDate: string; toDate: string }> => {
  const sorted = [...periods].sort((a, b) => a.fromDate.localeCompare(b.fromDate));
  if (sorted.length < 2) return [];

  const gaps: Array<{ fromDate: string; toDate: string }> = [];
  let coveredUntil = sorted[0].toDate || OPEN_END;

  for (const period of sorted.slice(1)) {
    if (coveredUntil >= OPEN_END) break;
    const expectedStart = addOneDay(coveredUntil);
    if (period.fromDate > expectedStart) {
      gaps.push({
        fromDate: expectedStart,
        toDate: subtractOneDay(period.fromDate),
      });
    }
    const periodEnd = period.toDate || OPEN_END;
    if (periodEnd > coveredUntil) coveredUntil = periodEnd;
  }

  return gaps;
};

export const hasPeriodOverlap = (
  fromDate: string,
  toDate: string | null,
  periods: Array<{ id?: string | number | null; fromDate: string; toDate: string | null }>,
): boolean => {
  const p1Start = fromDate;
  const p1End = toDate || "9999-12-31";

  for (const period of periods) {
    const p2Start = period.fromDate;
    const p2End = period.toDate || "9999-12-31";
    if (p1Start <= p2End && p2Start <= p1End) {
      return true;
    }
  }
  return false;
};

type ClosablePeriod = {
  id?: string | number | null;
  fromDate: string;
  toDate: string | null;
};

/**
 * Does a period get ended by a successor starting on `fromDate`?
 *
 * Mirrors `close_preceding_custom_periods` on the server. Announcing a rate
 * "from this date onwards" answers the open question of when the previous
 * one ended, so the predecessor gets the day before as its end. Only a
 * period the successor fully replaces qualifies: one that started earlier
 * and does not outlast the new one. A period reaching past the new end is
 * not a predecessor but a real overlap, and the editor still refuses it.
 */
export const isClosedBySuccessor = (
  period: ClosablePeriod,
  fromDate: string,
  toDate: string | null,
): boolean => {
  if (period.fromDate >= fromDate) return false;
  const periodEnd = period.toDate || OPEN_END;
  if (periodEnd < fromDate) return false;
  return periodEnd <= (toDate || OPEN_END);
};

/**
 * Apply the successor rule to a list of drafts, as the server will.
 *
 * Shown in the dialog before saving, so the list the user is looking at is
 * the plan that gets stored rather than a surprise on reload.
 */
export const closePrecedingPeriods = <T extends ClosablePeriod>(
  periods: T[],
  fromDate: string,
  toDate: string | null,
  excludeId?: string | number | null,
): T[] =>
  periods.map((period) => {
    if (
      excludeId !== undefined &&
      excludeId !== null &&
      String(period.id) === String(excludeId)
    ) {
      return period;
    }
    if (!isClosedBySuccessor(period, fromDate, toDate)) return period;
    return { ...period, toDate: subtractOneDay(fromDate) };
  });

/**
 * The "Teamleiter" ticks in the master data (one per department and one
 * for "Tätigkeiten ohne Abteilungszuordnung") belong to every level but
 * Mitarbeiter: a Mitarbeiter leads nothing. See
 * docs/teamleiter-je-abteilung.md.
 */
export const showsTeamleadTicks = (role: string | null | undefined): boolean =>
  role === "Admin" || role === "Personaler" || role === "Teamleiter";

/** Why the ticks cannot be saved like this, or null when they can: a
 * Teamleiter leads at least one department or the tasks without one. The
 * server applies the same rule. */
export const teamleadTickError = (
  role: string | null | undefined,
  teamleadDepartmentIds: ReadonlyArray<string | number> | null | undefined,
  teamleadForUnassigned: boolean | null | undefined,
): string | null =>
  role === "Teamleiter" &&
  (teamleadDepartmentIds ?? []).length === 0 &&
  !teamleadForUnassigned
    ? "Ein Teamleiter braucht mindestens einen Haken unter „Teamleiter“: eine Abteilung oder „Tätigkeiten ohne Abteilungszuordnung“."
    : null;
